import { describeWalletError } from "@/lib/walletError";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { AssetAmount } from "@sundaeswap/asset";
import { useHandleDepositRequest } from "@/hooks";
import { useHydrozoaHeadInfo } from "@/hooks/useHydrozoaScriptAddress";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { IntroStep } from "./IntroStep";
import { ConnectStep } from "./ConnectStep";
import { AmountStep, createDepositEntry, DepositEntry } from "./AmountStep";
import { ReviewStep } from "./ReviewStep";
import { HardwareWalletStep } from "./HardwareWalletStep";
import { ProcessingStep } from "./ProcessingStep";
import { SuccessStep } from "./SuccessStep";
import { formatAssetLabel, formatAssetValue } from "@/lib/marketUtils";
import { Core } from "@blaze-cardano/sdk";
import {
  competitionSessionKey,
  recordDepositSubmitted,
  registerCompetitionAccount,
  useCompetitionSession,
} from "@/hooks/useCompetitionAuth";
import { useQueryClient } from "@tanstack/react-query";
import { hashRules } from "../RulesView";

export type DepositStep =
  | "intro"
  | "connect"
  | "amount"
  | "hardware"
  | "review"
  | "processing"
  | "success";

interface DepositOverlayProps {
  open: boolean;
  onClose: () => void;
  onCloseStart?: () => void;
  viewRequestId?: string | null;
  initialAssetId?: string | null;
  /** Entries saved before an OAuth redirect: reopen straight at review. */
  resumeEntries?: DepositEntry[] | null;
}

const STEP_ORDER: DepositStep[] = ["intro", "connect", "amount", "hardware", "review", "processing", "success"];

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export const DepositOverlay: FC<DepositOverlayProps> = ({
  open,
  onClose,
  onCloseStart,
  viewRequestId,
  initialAssetId,
  resumeEntries,
}) => {
  const [step, setStep] = useState<DepositStep>("intro");
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [animKey, setAnimKey] = useState(0);
  const [animateStep, setAnimateStep] = useState(false);
  const [closing, setClosing] = useState(false);
  const [depositEntries, setDepositEntries] = useState<DepositEntry[]>([
    createDepositEntry(),
  ]);
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [agreedRules, setAgreedRules] = useState(false);
  const [showHandle, setShowHandle] = useState(false);
  // Re-asked on every deposit: it gates funds, so a stale tick from an earlier
  // run of the wizard should not carry it.
  const [hardwareAcknowledged, setHardwareAcknowledged] = useState(false);
  const queryClient = useQueryClient();
  const { data: competitionSession } = useCompetitionSession();

  // Depositing re-registers, and registration overwrites show_handle with
  // whatever this box says. Left hard-coded to false, a returning user who had
  // already opted in would see it unticked and be silently opted back out. So
  // seed it from the saved answer -- but stop once they touch it, or their own
  // change would be undone by the next session refetch. The flag resets when
  // the overlay opens, so a later deposit seeds afresh.
  const showHandleTouched = useRef(false);
  useEffect(() => {
    if (open) showHandleTouched.current = false;
  }, [open]);
  useEffect(() => {
    if (showHandleTouched.current) return;
    if (competitionSession?.showHandle !== undefined) {
      setShowHandle(competitionSession.showHandle);
    }
  }, [competitionSession?.showHandle]);

  const handleShowHandleChange = useCallback((next: boolean) => {
    showHandleTouched.current = true;
    setShowHandle(next);
  }, []);

  const navigateTo = useCallback((next: DepositStep) => {
    const curIdx = STEP_ORDER.indexOf(step);
    const nextIdx = STEP_ORDER.indexOf(next);
    setDirection(nextIdx >= curIdx ? "forward" : "back");
    setAnimateStep(true);
    setAnimKey((k) => k + 1);
    setStep(next);
  }, [step]);
  const { activeWallet, mainAddress } = useWalletObserver();
  const { mutateAsync } = useHandleDepositRequest();
  const { depositTxs, ledgerView } = useGummiwormContext();
  const hasL2Funds = ledgerView.account !== null;
  const headInfo = useHydrozoaHeadInfo();
  const headOnline = !!headInfo;

  const isConnected = !!activeWallet;

  // When opened, decide which step to start on (only on open/viewRequestId change)
  useEffect(() => {
    if (!open) return;
    setAnimateStep(false);
    setHardwareAcknowledged(false);
    if (viewRequestId) {
      setActiveRequestId(viewRequestId);
      setStep("processing");
    } else if (resumeEntries && resumeEntries.length > 0) {
      // Returning from the OAuth redirect: pick the deposit back up at the
      // review step (or at connect if the wallet has not reattached yet —
      // the connect→amount auto-advance then leaves one click to review).
      setActiveRequestId(null);
      setDepositEntries(resumeEntries);
      setStep(isConnected ? "review" : "connect");
    } else {
      setActiveRequestId(null);
      setDepositEntries([createDepositEntry(initialAssetId ?? undefined)]);
      setStep(initialAssetId ? (isConnected ? "amount" : "connect") : "intro");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, viewRequestId, initialAssetId, resumeEntries, isConnected]);

  // When wallet connects while on the connect step, advance to amount
  useEffect(() => {
    if (step === "connect" && isConnected) {
      setStep("amount");
    }
  }, [step, isConnected]);

  const handleClose = useCallback(() => {
    setClosing(true);
    onCloseStart?.();
    setTimeout(() => {
      setClosing(false);
      setAnimateStep(false);
      setStep("intro");
      setDepositEntries([createDepositEntry()]);
      setActiveRequestId(null);
      setError(null);
      setAgreedRules(false);
      onClose();
    }, 200);
  }, [onClose, onCloseStart]);

  const handleBeginDeposit = useCallback(() => {
    if (isConnected) {
      navigateTo("amount");
    } else {
      navigateTo("connect");
    }
  }, [isConnected, navigateTo]);

  const handleAmountConfirm = useCallback(() => {
    navigateTo("hardware");
  }, [navigateTo]);

  const handleHardwareContinue = useCallback(() => {
    navigateTo("review");
  }, [navigateTo]);

  const handleDeposit = useCallback(async () => {
    setError(null);
    navigateTo("processing");
    try {
      // Record the registration + rules agreement first: the api binds the
      // signed-in X identity to this deposit account and stores the consent
      // (rules hash, ip, timestamp). A rejection (e.g. the X account is
      // already bound elsewhere) stops the deposit before any tx is built.
      const accountId = mainAddress
        ? Core.Address.fromBech32(mainAddress).getProps().paymentPart?.hash
        : undefined;
      if (!accountId) {
        throw new Error("Wallet address unavailable; reconnect and try again.");
      }
      await registerCompetitionAccount({
        accountId,
        rulesSha256: await hashRules(),
        showHandle,
      });
      // Registration is what writes show_handle, so the cached session is now
      // out of date. It has a 60s staleTime, and the leaderboard's toggle
      // renders straight from it -- without this the box the user just ticked
      // reads back unticked, as though the choice had been dropped.
      queryClient.invalidateQueries({ queryKey: competitionSessionKey });

      const amounts = depositEntries
        .filter((e) => Number(e.amount) > 0)
        .map((e) =>
          AssetAmount.fromValue(Number(e.amount), {
            decimals: 6,
            assetId: e.assetId,
          }),
        );
      const result = await mutateAsync({ depositAmounts: amounts });
      const requestId = (result as { requestId: number }).requestId;
      setActiveRequestId(requestId.toString());
      void recordDepositSubmitted(requestId);
    } catch (err) {
      setError(describeWalletError(err));
      navigateTo("review");
    }
  }, [depositEntries, mutateAsync, navigateTo, mainAddress]);

  const handleBack = useCallback(() => {
    if (step === "connect") {
      navigateTo("intro");
      return;
    }
    if (step === "amount") {
      navigateTo("intro");
      return;
    }
    if (step === "hardware") {
      navigateTo("amount");
      return;
    }
    if (step === "review") {
      navigateTo("hardware");
    }
  }, [navigateTo, step]);

  const activeTx = activeRequestId
    ? depositTxs.get(activeRequestId)
    : null;
  const activeLedgerDeposit = useMemo(() => {
    if (!activeRequestId) return null;
    return (
      Object.values(ledgerView.deposits).find(
        (d) => d.requestId.toString() === activeRequestId,
      ) ?? null
    );
  }, [activeRequestId, ledgerView.deposits]);
  // Provisioned refund tx, preferring local tracking but falling back to the
  // ledger view for deposits only known remotely (e.g. viewed on another tab)
  const refundInfo = useMemo(() => {
    const cbor =
      activeTx?.refundCbor ||
      (activeLedgerDeposit?.refundTx
        ? Buffer.from(activeLedgerDeposit.refundTx).toString("hex")
        : "");
    if (!cbor) return null;
    let hash = activeTx?.refundTxHash;
    if (!hash) {
      try {
        hash = Core.Transaction.fromCbor(Core.TxCBOR(cbor)).getId();
      } catch {
        hash = undefined;
      }
    }
    return { cbor, hash };
  }, [activeTx?.refundCbor, activeTx?.refundTxHash, activeLedgerDeposit]);
  // Derive status from SpacetimeDB deposit row if no local tracking exists
  const activeStatus = activeTx?.status ?? (() => {
    const dep = activeLedgerDeposit;
    if (!dep) return null;
    if (dep.absorbedInBlock) return "absorbed" as const;
    if (dep.rejectedInBlock) return "rejected" as const;
    if (dep.failureReason) return "failed" as const;
    if (dep.refundTx) return "confirmed" as const;
    return "pending" as const;
  })();

  const primaryDepositAmount = depositEntries[0]?.amount ?? "";

  const effectiveAmountLabel = useMemo(() => {
    if (!activeRequestId) return primaryDepositAmount ? `${primaryDepositAmount} ADA` : "";
    const dep = Object.values(ledgerView.deposits).find(
      (d) => d.requestId.toString() === activeRequestId,
    );
    if (!dep) return primaryDepositAmount ? `${primaryDepositAmount} ADA` : "";
    const assets = dep.value.assets;
    if (assets.length === 0) return "";
    const first = assets[0];
    const primary = `${formatAssetValue(first.asset, BigInt(first.value))} ${formatAssetLabel(first.asset)}`;
    return assets.length > 1
      ? `${primary} + ${assets.length - 1} other${assets.length > 2 ? "s" : ""}`
      : primary;
  }, [activeRequestId, ledgerView.deposits, primaryDepositAmount]);

  // ProcessingStep now handles the complete state directly (shows "Funds Deposited!")

  // Build the ordered list of steps for this session
  const steps = useMemo<DepositStep[]>(() => {
    const s: DepositStep[] = ["intro"];
    if (!isConnected) s.push("connect");
    s.push("amount", "review", "processing", "success");
    return s;
  }, [isConnected]);

  const currentIndex = steps.indexOf(step);
  const showBackButton =
    currentIndex > 0 &&
    (step === "connect" ||
      step === "amount" ||
      step === "hardware" ||
      step === "review");
  const useBottomSheetLayout = step === "processing" || step === "success";

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <div
      className={`sr-overlay ${useBottomSheetLayout ? "justify-end px-0 pt-4 sm:justify-center sm:p-4" : ""}`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        aria-hidden="true"
        className={`sr-overlay-backdrop ${closing ? "sr-overlay-backdrop-exit" : "sr-overlay-backdrop-enter"}`}
      />
      <button
        onClick={handleClose}
        className={`fixed right-4 top-4 z-20 rounded-full border border-white/[0.03] bg-sr-surface p-2 text-sr-neutral-500 transition-colors hover:text-white sm:right-6 sm:top-6 ${useBottomSheetLayout ? "hidden sm:block" : ""}`}
        aria-label="Close deposit flow"
      >
        <CloseIcon />
      </button>
      <div
        key={animKey}
        className={`relative z-10 flex w-full max-w-[420px] flex-col gap-8 items-center ${closing ? "sr-overlay-content-exit" : "sr-overlay-content-enter"} ${
          useBottomSheetLayout
            ? "mt-auto gap-0 rounded-t-[32px] bg-sr-surface pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:mt-0 sm:gap-8 sm:rounded-none sm:bg-transparent sm:pt-0 sm:pb-0"
            : ""
        }`}
      >
        <div
          className={`w-full flex flex-col gap-8 items-center ${animateStep ? (direction === "forward" ? "sr-step-forward" : "sr-step-back") : ""} ${useBottomSheetLayout ? "gap-0 px-0 sm:gap-8 sm:px-0" : ""}`}
        >
          {step === "intro" && (
            <IntroStep onBegin={handleBeginDeposit} isConnected />
          )}
          {step === "connect" && (
            <ConnectStep onBack={showBackButton ? handleBack : undefined} />
          )}
          {step === "amount" && (
            <AmountStep
              entries={depositEntries}
              onEntriesChange={setDepositEntries}
              onConfirm={handleAmountConfirm}
              onBack={showBackButton ? handleBack : undefined}
            />
          )}
          {step === "hardware" && (
            <HardwareWalletStep
              acknowledged={hardwareAcknowledged}
              onAcknowledgedChange={setHardwareAcknowledged}
              onContinue={handleHardwareContinue}
              onBack={showBackButton ? handleBack : undefined}
            />
          )}

          {step === "review" && (
            <ReviewStep
              entries={depositEntries}
              error={error}
              headOnline={headOnline}
              agreedRules={agreedRules}
              onAgreedRulesChange={setAgreedRules}
              showHandle={showHandle}
              onShowHandleChange={handleShowHandleChange}
              onConfirm={handleDeposit}
              onBack={showBackButton ? handleBack : undefined}
              estimatedSeconds={
                headInfo?.absorptionStartOffsetSeconds
                  ? 121 + headInfo.absorptionStartOffsetSeconds
                  : null
              }
            />
          )}
          {step === "processing" && (
            <ProcessingStep
              requestId={activeRequestId}
              status={activeStatus ?? null}
              error={
                error ??
                activeTx?.errorMessage ??
                activeLedgerDeposit?.failureReason ??
                null
              }
              estimatedAbsorptionTime={activeTx?.estimatedAbsorptionTime}
              amountLabel={effectiveAmountLabel}
              txHash={activeTx?.hash}
              hardConfirmed={activeTx?.hardConfirmed}
              refundTxHash={refundInfo?.hash}
              refundCbor={refundInfo?.cbor}
            />
          )}
          {step === "success" && (
            <SuccessStep amountLabel={effectiveAmountLabel} onClose={handleClose} />
          )}

          {useBottomSheetLayout && (
            <div className="w-full px-4 pt-0 sm:hidden">
              <button
                onClick={handleClose}
                className="w-full rounded-full border border-white/[0.06] bg-white/[0.03] px-4 py-3 text-sm font-medium text-sr-neutral-300 transition-colors hover:bg-white/[0.05] hover:text-white"
              >
                Close
              </button>
            </div>
          )}

          {/* Step indicator dots (hidden during processing/success) */}
          {step !== "processing" && step !== "success" && (
            <div className="flex gap-1 items-center justify-center">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={i === currentIndex ? "sr-dot-active" : "sr-dot"}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
